import React, {useState} from 'react';
import {Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Paper, TextField, Typography} from "@mui/material";
import {Email, Lock, Visibility, VisibilityOff} from '@mui/icons-material';
import '@fontsource/poppins';

export default function LoginTemplate(props) {
    const { email, password, onChange, login, waitingForServer } = props;
    const [showPassword, setShowPassword] = useState(false);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '10vh 0',
            }}
        >
            <Box sx={{ width: '100%', maxWidth: '450px' }}>
                <Paper
                    elevation={24}
                    sx={{
                        p: 5,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        background: 'white',
                        borderRadius: '24px',
                        position: 'relative',
                        zIndex: 1,
                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                        },
                    }}
                >
                    {/* Animated Icon */}
                    <Box
                        sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 3,
                            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.4)',
                            animation: 'float 3s ease-in-out infinite',
                            '@keyframes float': {
                                '0%, 100%': { transform: 'translateY(0px)' },
                                '50%': { transform: 'translateY(-10px)' },
                            },
                        }}
                    >
                        <Lock sx={{ fontSize: 40, color: 'white' }} />
                    </Box>

                    {/* Title Section */}
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            mb: 1,
                            fontFamily: 'Poppins, sans-serif',
                        }}
                    >
                        Bejelentkezés
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary',
                            mb: 4,
                            fontFamily: 'Poppins, sans-serif',
                        }}
                    >
                        Kérlek, jelentkezz be a folytatáshoz
                    </Typography>

                    {/* Form */}
                    <Box component="form" id="login" onSubmit={login} sx={{ width: '100%' }}>
                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="E-mail"
                            name="email"
                            autoComplete="off"
                            autoFocus
                            onChange={onChange}
                            value={email}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    transition: 'all 0.3s ease',
                                    '& fieldset': {
                                        borderWidth: '2px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#667eea',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                        borderWidth: '2px',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: 'Poppins, sans-serif',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#667eea',
                                },
                            }}
                        />

                        <TextField
                            variant="outlined"
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Jelszó"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="off"
                            onChange={onChange}
                            value={password}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: '#667eea' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            sx={{
                                                color: '#667eea',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(102, 126, 234, 0.1)',
                                                }
                                            }}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                                    transition: 'all 0.3s ease',
                                    '& fieldset': {
                                        borderWidth: '2px',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#667eea',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                        borderWidth: '2px',
                                    },
                                },
                                '& .MuiInputLabel-root': {
                                    fontFamily: 'Poppins, sans-serif',
                                },
                                '& .MuiInputLabel-root.Mui-focused': {
                                    color: '#667eea',
                                },
                            }}
                        />

                        {props.errorMessage && (
                            <Alert
                                severity="error"
                                sx={{
                                    mt: 2,
                                    borderRadius: '12px',
                                    borderLeft: '4px solid #d32f2f',
                                    fontFamily: 'Poppins, sans-serif',
                                    animation: 'shake 0.5s ease',
                                    '@keyframes shake': {
                                        '0%, 100%': { transform: 'translateX(0)' },
                                        '25%': { transform: 'translateX(-10px)' },
                                        '75%': { transform: 'translateX(10px)' },
                                    },
                                }}
                            >
                                {props.errorMessage}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={waitingForServer}
                            sx={{
                                mt: 3,
                                mb: 2,
                                py: 1.5,
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontWeight: 600,
                                fontSize: '1rem',
                                textTransform: 'none',
                                fontFamily: 'Poppins, sans-serif',
                                boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)',
                                },
                                '&:active': {
                                    transform: 'translateY(0)',
                                },
                                '&:disabled': {
                                    background: 'linear-gradient(135deg, #ccc 0%, #999 100%)',
                                },
                            }}
                        >
                            {waitingForServer ? (
                                <CircularProgress size={24} sx={{ color: 'white' }} />
                            ) : (
                                'Belépés'
                            )}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    );
}